import type {
  ClientDepositRecord,
  ClientHotLeadRecord,
  ClientStatusSettings,
  MessageContent,
  MessageReplyPreview,
  TrustAssetKind
} from "@tg/shared";
import { httpBaseFromWsUrl } from "./auth-api.js";

export interface FrequentChatEntry {
  chatId: string;
  title: string;
}

export interface FolderLinesResponse {
  enabled: boolean;
  myLineId: string | null;
  // outOfGeo — линия чужого гео, уже привязанная к папке этого аккаунта:
  // показать и дать снять, но не предлагать для новых привязок.
  lines: Array<{ lineId: string; title: string; outOfGeo?: boolean }>;
  bindings: Record<string, string[]>;
}

export interface InternalDeletedMessagePayload {
  messageId: string;
  senderId: string;
  senderName?: string;
  replyTo?: MessageReplyPreview;
  isEdited?: boolean;
  outgoing: boolean;
  content: MessageContent;
  internalAuthorName?: string;
  occurredAt: string;
}

export interface InternalDeletedMessageRecord {
  eventId: string;
  accountId: string;
  chatId: string;
  messageId: string;
  payload: InternalDeletedMessagePayload;
  deletedAt: string;
}

export interface InternalLocalFolderRecord {
  folderId: string;
  accountId: string;
  title: string;
  iconName?: string | null;
  chatLimit?: number | null;
  includedChatIds: string[];
  pinnedChatIds?: string[];
  position?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface InternalTrustAssetRecord {
  assetId: string;
  accountId: string;
  title: string;
  description?: string;
  supportDescription?: string;
  mediaKind: TrustAssetKind;
  content: MessageContent;
  // Доп. медиа шаблона (мультифото) сверх основного content.
  extraContents?: MessageContent[];
  // false — мультифото уходит раздельными сообщениями; иначе — одним альбомом.
  groupMedia?: boolean;
  sectionId?: string;
  createdByUserId?: string;
  createdByLogin?: string;
  createdAt: string;
  updatedAt: string;
}

// Отметка «этот шаблон уже отправляли в этот чат». source: auto — записана
// автоматически при отправке из панели шаблонов; manual — саппорт поставил
// её сам кнопкой.
export interface InternalTrustAssetUsageRecord {
  assetId: string;
  chatId: string;
  usedCount: number;
  source: "auto" | "manual";
  actorLogin?: string;
  firstUsedAt: string;
  lastUsedAt: string;
}

// Раздел (parentId = null) или подраздел (parentId = sectionId раздела)
// шаблонов. С 2026-08-05 структура per-account — свой набор у каждого
// аккаунта.
export interface InternalTemplateSectionRecord {
  sectionId: string;
  accountId: string;
  parentId: string | null;
  title: string;
  createdAt: string;
  updatedAt: string;
}

// Языки переводчика: код + подпись; prompt приходит только админу.
export interface TranslatorLanguageRecord {
  code: string;
  label: string;
  prompt?: string;
}

export interface TranslatorAccountBindingRecord {
  languages?: string[];
  defaultLanguage?: string;
}

export interface TranslatorConfigResponse {
  languages: TranslatorLanguageRecord[];
  accountBindings: Record<string, TranslatorAccountBindingRecord>;
  /** Овнеру: языки каждого гео-админа (для обзора). */
  adminLanguages?: Array<{
    ownerUserId: string;
    ownerLogin: string;
    languages: TranslatorLanguageRecord[];
  }>;
  /** Гео-админу: его собственные языки (без базового RU). */
  ownLanguages?: TranslatorLanguageRecord[];
}

// Личный текстовый скрипт саппорта (раздел «Скрипты» в шаблонах).
export interface SupportScriptRecord {
  scriptId: string;
  ownerUserId: string;
  title: string;
  content: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminSupportScriptRecord extends SupportScriptRecord {
  ownerLogin: string;
}

const request = async <TResponse>(url: string, init?: RequestInit): Promise<TResponse> => {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...init
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Request failed with status ${response.status}`);
  }
  return (await response.json()) as TResponse;
};

export const internalApi = {
  getChatTags(accountId: string, chatId: string, wsUrl: string): Promise<{ tags: string[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ tags: string[] }>(
      `${base}/api/internal/chat-tags?accountId=${encodeURIComponent(accountId)}&chatId=${encodeURIComponent(chatId)}`
    );
  },
  updateChatTags(
    accountId: string,
    chatId: string,
    tags: string[],
    wsUrl: string
  ): Promise<{ tags: string[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ tags: string[] }>(`${base}/api/internal/chat-tags`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, chatId, tags })
    });
  },
  getChatNote(
    accountId: string,
    chatId: string,
    wsUrl: string
  ): Promise<{ note: string; updatedAt: string | null; updatedByUserId: string | null }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(
      `${base}/api/internal/chat-note?accountId=${encodeURIComponent(accountId)}&chatId=${encodeURIComponent(chatId)}`
    );
  },
  updateChatNote(
    accountId: string,
    chatId: string,
    note: string,
    wsUrl: string
  ): Promise<{ note: string; updatedAt: string | null; updatedByUserId: string | null }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/internal/chat-note`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, chatId, note })
    });
  },
  listDeletedMessages(
    accountId: string,
    chatId: string,
    wsUrl: string,
    limit = 50
  ): Promise<{ messages: InternalDeletedMessageRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ messages: InternalDeletedMessageRecord[] }>(
      `${base}/api/internal/deleted-messages?accountId=${encodeURIComponent(accountId)}&chatId=${encodeURIComponent(chatId)}&limit=${encodeURIComponent(String(limit))}`
    );
  },
  // Общий поиск по удалённым сообщениям аккаунта (запрос №29): текст или
  // подпись медиа, подстрока без учёта регистра, все чаты, новые сверху.
  searchDeletedMessages(
    accountId: string,
    query: string,
    wsUrl: string,
    limit = 50
  ): Promise<{ messages: InternalDeletedMessageRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ messages: InternalDeletedMessageRecord[] }>(
      `${base}/api/internal/deleted-messages/search?accountId=${encodeURIComponent(accountId)}&q=${encodeURIComponent(query)}&limit=${encodeURIComponent(String(limit))}`
    );
  },
  listTrustAssets(
    accountId: string,
    wsUrl: string
  ): Promise<{ assets: InternalTrustAssetRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ assets: InternalTrustAssetRecord[] }>(
      `${base}/api/internal/trust-assets?accountId=${encodeURIComponent(accountId)}`
    );
  },
  createTrustAsset(
    input: {
      accountId: string;
      title: string;
      description?: string;
      supportDescription?: string;
      mediaKind: TrustAssetKind;
      content: MessageContent;
      extraContents?: MessageContent[];
      groupMedia?: boolean;
      sectionId?: string | null;
    },
    wsUrl: string
  ): Promise<{ asset: InternalTrustAssetRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ asset: InternalTrustAssetRecord }>(`${base}/api/internal/trust-assets`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    });
  },
  // Правка сохранённого шаблона: content/extraContents/mediaKind шлём только
  // когда медиа реально менялось; пустая строка description = очистить.
  updateTrustAsset(
    input: {
      accountId: string;
      assetId: string;
      title?: string;
      description?: string;
      supportDescription?: string;
      mediaKind?: TrustAssetKind;
      content?: MessageContent;
      extraContents?: MessageContent[];
      groupMedia?: boolean;
    },
    wsUrl: string
  ): Promise<{ asset: InternalTrustAssetRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ asset: InternalTrustAssetRecord }>(
      `${base}/api/internal/trust-assets/update`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  // Перемещение шаблонов в раздел (sectionId null = «Без раздела»).
  moveTrustAssets(
    input: { accountId: string; assetIds: string[]; sectionId: string | null },
    wsUrl: string
  ): Promise<{ moved: number }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ moved: number }>(`${base}/api/internal/trust-assets/move`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    });
  },
  // Копирование шаблонов. targetSectionId не передан — копия остаётся в
  // разделе оригинала; null — «Без раздела»; строка — целевой раздел.
  copyTrustAssets(
    input: { accountId: string; assetIds: string[]; targetSectionId?: string | null },
    wsUrl: string
  ): Promise<{ assets: InternalTrustAssetRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ assets: InternalTrustAssetRecord[] }>(
      `${base}/api/internal/trust-assets/copy`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  deleteTrustAsset(
    accountId: string,
    assetId: string,
    wsUrl: string
  ): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/internal/trust-assets/delete`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, assetId })
    });
  },
  listTrustAssetUsage(
    accountId: string,
    chatId: string,
    wsUrl: string
  ): Promise<{ usage: InternalTrustAssetUsageRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ usage: InternalTrustAssetUsageRecord[] }>(
      `${base}/api/internal/trust-assets/usage?accountId=${encodeURIComponent(
        accountId
      )}&chatId=${encodeURIComponent(chatId)}`
    );
  },
  setTrustAssetUsage(
    input: { accountId: string; chatId: string; assetId: string; used: boolean },
    wsUrl: string
  ): Promise<{ usage: InternalTrustAssetUsageRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ usage: InternalTrustAssetUsageRecord[] }>(
      `${base}/api/internal/trust-assets/usage`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  listTemplateSections(
    accountId: string,
    wsUrl: string
  ): Promise<{ sections: InternalTemplateSectionRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ sections: InternalTemplateSectionRecord[] }>(
      `${base}/api/internal/template-sections?accountId=${encodeURIComponent(accountId)}`
    );
  },
  createTemplateSection(
    input: { accountId: string; title: string; parentId?: string | null },
    wsUrl: string
  ): Promise<{ section: InternalTemplateSectionRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ section: InternalTemplateSectionRecord }>(
      `${base}/api/internal/template-sections`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  renameTemplateSection(
    input: { accountId: string; sectionId: string; title: string },
    wsUrl: string
  ): Promise<{ section: InternalTemplateSectionRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ section: InternalTemplateSectionRecord }>(
      `${base}/api/internal/template-sections/update`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  deleteTemplateSection(
    accountId: string,
    sectionId: string,
    wsUrl: string
  ): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/internal/template-sections/delete`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, sectionId })
    });
  },
  // Папки по линиям (раунд 46): что нужно SPA для фильтра вкладок папок.
  // Статусы клиентов по депозитам: вся карта «telegramUserId → последний
  // депозит» + пороги. SPA сопоставляет по telegramUserId своих чатов.
  getClientDeposits(
    accountId: string,
    wsUrl: string
  ): Promise<{
    records: ClientDepositRecord[];
    settings: ClientStatusSettings;
    hotLeads?: ClientHotLeadRecord[];
  }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{
      records: ClientDepositRecord[];
      settings: ClientStatusSettings;
      hotLeads?: ClientHotLeadRecord[];
    }>(
      `${base}/api/internal/client-deposits?accountId=${encodeURIComponent(accountId)}`
    );
  },
  getFolderLines(accountId: string, wsUrl: string): Promise<FolderLinesResponse> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<FolderLinesResponse>(
      `${base}/api/internal/folder-lines?accountId=${encodeURIComponent(accountId)}`
    );
  },
  setFolderLineBinding(
    input: { accountId: string; listId: string; lineIds: string[] },
    wsUrl: string
  ): Promise<{ bindings: Record<string, string[]> }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ bindings: Record<string, string[]> }>(`${base}/api/internal/folder-lines`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    });
  },
  getFolderLinesSetting(
    wsUrl: string
  ): Promise<{ enabled: boolean; stored: boolean | null; inherited: boolean }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/admin/folder-lines/setting`);
  },
  setFolderLinesSetting(
    input: { enabled?: boolean; clear?: boolean },
    wsUrl: string
  ): Promise<{ enabled: boolean }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request(`${base}/api/admin/folder-lines/setting`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    });
  },
  // «Частые группы» (accountId → список чатов) живут на сервере: для
  // саппорта это whitelist адресатов быстрой пересылки, поэтому локальной
  // настройке доверять нельзя. Читают все, у кого есть доступ к аккаунту;
  // PUT — только менеджер.
  listFrequentChats(
    wsUrl: string
  ): Promise<{ frequentChats: Record<string, FrequentChatEntry[]> }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ frequentChats: Record<string, FrequentChatEntry[]> }>(
      `${base}/api/internal/frequent-chats`
    );
  },
  setFrequentChat(
    input: { accountId: string; chatId: string; title?: string; action: "add" | "remove" },
    wsUrl: string
  ): Promise<{ frequentChats: Record<string, FrequentChatEntry[]> }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ frequentChats: Record<string, FrequentChatEntry[]> }>(
      `${base}/api/internal/frequent-chats`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  // Скрипты саппорта: свои читает/создаёт/правит каждый; админ видит все.
  listSupportScripts(wsUrl: string): Promise<{ scripts: SupportScriptRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ scripts: SupportScriptRecord[] }>(`${base}/api/support/scripts`);
  },
  createSupportScript(
    input: { title: string; content: string; description?: string },
    wsUrl: string
  ): Promise<{ script: SupportScriptRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ script: SupportScriptRecord }>(`${base}/api/support/scripts`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    });
  },
  updateSupportScript(
    scriptId: string,
    input: { title?: string; content?: string; description?: string | null },
    wsUrl: string
  ): Promise<{ script: SupportScriptRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ script: SupportScriptRecord }>(
      `${base}/api/support/scripts/${encodeURIComponent(scriptId)}`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  deleteSupportScript(scriptId: string, wsUrl: string): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(
      `${base}/api/support/scripts/${encodeURIComponent(scriptId)}`,
      { method: "DELETE" }
    );
  },
  adminListSupportScripts(
    wsUrl: string
  ): Promise<{ scripts: AdminSupportScriptRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ scripts: AdminSupportScriptRecord[] }>(
      `${base}/api/admin/support-scripts`
    );
  },
  // Конфиг переводчика: языки (для селектора в композере) и привязки к
  // аккаунтам (какие языки видны и какой — по умолчанию). Читают все,
  // изменения — только админ.
  getTranslatorConfig(wsUrl: string): Promise<TranslatorConfigResponse> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<TranslatorConfigResponse>(`${base}/api/translator/config`);
  },
  setTranslatorLanguages(
    languages: Array<{ code: string; label: string; prompt: string }>,
    wsUrl: string
  ): Promise<{ languages: TranslatorLanguageRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ languages: TranslatorLanguageRecord[] }>(
      `${base}/api/admin/translator/languages`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ languages })
      }
    );
  },
  setTranslatorBinding(
    input: {
      accountId: string;
      languages?: string[];
      defaultLanguage?: string;
      clear?: boolean;
    },
    wsUrl: string
  ): Promise<{ accountBindings: Record<string, TranslatorAccountBindingRecord> }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ accountBindings: Record<string, TranslatorAccountBindingRecord> }>(
      `${base}/api/admin/translator/bindings`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  listLocalFolders(
    accountId: string,
    wsUrl: string
  ): Promise<{ folders: InternalLocalFolderRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ folders: InternalLocalFolderRecord[] }>(
      `${base}/api/internal/local-folders?accountId=${encodeURIComponent(accountId)}`
    );
  },
  createLocalFolder(
    input: {
      accountId: string;
      title: string;
      iconName?: string;
      chatLimit?: number | null;
      includedChatIds: string[];
    },
    wsUrl: string
  ): Promise<{ folder: InternalLocalFolderRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ folder: InternalLocalFolderRecord }>(
      `${base}/api/internal/local-folders`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  reorderLocalFolders(
    input: {
      accountId: string;
      positions: Array<{ folderId: string; position: number }>;
    },
    wsUrl: string
  ): Promise<{ folders: InternalLocalFolderRecord[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ folders: InternalLocalFolderRecord[] }>(
      `${base}/api/internal/local-folders/order`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  updateLocalFolder(
    input: {
      accountId: string;
      folderId: string;
      title?: string;
      iconName?: string | null;
      chatLimit?: number | null;
      includedChatIds?: string[];
      // Дельта состава — предпочтительный способ: сервер применяет её к
      // актуальной строке, снимок вкладки может быть устаревшим.
      addChatIds?: string[];
      removeChatIds?: string[];
      pinnedChatIds?: string[];
    },
    wsUrl: string
  ): Promise<{ folder: InternalLocalFolderRecord }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ folder: InternalLocalFolderRecord }>(
      `${base}/api/internal/local-folders`,
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      }
    );
  },
  deleteLocalFolder(
    accountId: string,
    folderId: string,
    wsUrl: string
  ): Promise<{ ok: true }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ ok: true }>(`${base}/api/internal/local-folders`, {
      method: "DELETE",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ accountId, folderId })
    });
  },
  // Конвертация папки: "folder:<id>" (Telegram) → папка клиента и
  // "local:<id>" → папка Telegram. dryRun — предпросмотр без изменений.
  // position — индекс вкладки в общем ряду, чтобы новая папка клиента
  // встала на место исходной.
  convertFolder(
    input: { accountId: string; listId: string; dryRun?: boolean; position?: number },
    wsUrl: string
  ): Promise<FolderConvertResult> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<FolderConvertResult>(`${base}/api/internal/local-folders/convert`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(input)
    });
  }
};

export interface FolderConvertResult {
  ok: true;
  dryRun: boolean;
  direction: "tg-to-local" | "local-to-tg";
  // Название исходной папки и то, под которым будет/создана новая.
  title: string;
  resultTitle: string;
  chatCount: number;
  pinnedCount: number;
  hasFilters: boolean;
  warnings: string[];
  // Только при реальной конвертации.
  newListId?: string;
  sourceDeleted?: boolean;
}
